// Central image manifest for the homepage and brand assets.
//
// Every image used by the homepage is declared here so paths can be swapped in
// one place without hunting through components. All paths are served from
// `public/`, so `/images/foo.jpg` maps to `public/images/foo.jpg`.
//
// NOTE: the colour-scheme shots used by the Colour Catalogue
//   (luxurious-, oceanic-, enchanted-, monochromatic-, urban-, golden- and
//   pristine-{interior,exterior}.jpg) are generated DEMO placeholders built
//   from each scheme palette. Swap them for real photography when available.

/** Official Vertex Paints logo. Used in the navbar, footer and app loader. */
export const BRAND_LOGO = '/images/brand/vertex-paints-logo.png';

export const heroImages = {
  /** Wide 5376x2267 colour-blocked interior — the main hero backdrop. */
  main: '/images/web/auto/interior-banner.jpg',
};

export type HeroSlide = {
  image: string;
  alt: string;
  eyebrow: string;
  /** Two-line headline: the second line is rendered in the gold gradient. */
  headline: [string, string];
  lead: string;
};

/** Auto-advancing hero backdrop — three slides, cross-faded every 6 seconds. */
export const heroSlides: HeroSlide[] = [
  {
    image: '/images/web/hero-interior.jpg',
    alt: 'A living room finished in colour-blocked Vertex Paints interior emulsion',
    eyebrow: 'Interior emulsions',
    headline: ['Colour that lasts.', 'Finish you can feel.'],
    lead:
      'Washable, low-odour interior finishes in matt, soft sheen and high sheen, tinted to over 1,800 shades — so every room keeps looking freshly painted.',
  },
  {
    image: '/images/web/house-modern.jpg',
    alt: 'A modern two-storey house coated in Vertex exterior emulsion',
    eyebrow: 'Exterior emulsions',
    headline: ['Built for the monsoon.', 'Beautiful all year.'],
    lead:
      'UV-stable, water-repellent exterior systems graded for Indian sun, rain and dust — façades that hold their colour without chalking, peeling or algae.',
  },
  {
    image: '/images/web/hero-pastel.jpg',
    alt: 'A pastel interior with a yellow chair and colourful vases',
    eyebrow: 'Colour Mode tinting',
    headline: ['Every shade you imagine,', 'matched at the counter.'],
    lead:
      'Pick from curated colour series or bring a swatch — Vertex Colour Mode tints the exact shade, the same way every time you come back for more.',
  },
  {
    image: '/images/web/house-dusk.jpg',
    alt: 'A family home at dusk with a freshly painted exterior',
    eyebrow: 'Waterproofing systems',
    headline: ['Keep water out.', 'Keep the colour in.'],
    lead:
      'Roof coatings, damp-proof putties and crack-bridging primers that stop leaks at the source — tested systems, not quick fixes.',
  },
];

export const interiorImages = {
  living: '/images/IB-livingroom.jpg',
  bedroom: '/images/IB-bedroom.jpg',
  study: '/images/IB-studyroom.jpg',
  swatchRoom: '/images/Interior-Paints.webp',
};

export const exteriorImages = {
  lattice: '/images/Lattice House-A.jpg',
  beach: '/images/Beach House-A.jpg',
  country: '/images/Country House-A.jpg',
  arched: '/images/Arched Residence-A.jpg',
  banner: '/images/Exterior-banner.jpeg',
};

/**
 * Room / elevation photography for the homepage product cards, keyed by the
 * product's catalogue route. Products are read from `src/data/catalog.ts`, so
 * the titles and links always match a real page; this map only supplies the
 * lifestyle photo (and a short tag) to show instead of the pack shot.
 */
export const productPhotos: Record<string, { image: string; alt: string; tag: string }> = {
  '/ExteriorWallPaint/PremiumEmlusion': {
    image: '/images/products/premium-emulsion-living.jpg',
    alt: 'A navy and wood living room finished in Vertex Premium Emulsion',
    tag: 'Rich colour',
  },
  '/ExteriorWallPaint/SilkShine_SuperGloss': {
    image: '/images/products/silk-shine-super-gloss-lounge.jpg',
    alt: 'A lounge with a high-gloss magenta wall in Silk Shine Super Gloss',
    tag: 'High gloss',
  },
  '/InteriorWallPaint/EasyWashLuxuryEmulsion': {
    image: '/images/products/easy-wash-painter.jpg',
    alt: 'Living room walls finished in Easy Wash Luxury Emulsion',
    tag: 'Easy wash',
  },
  '/InteriorWallPaint/SilkShine_PearlGloss': {
    image: interiorImages.bedroom,
    alt: 'Bedroom walls finished in Silk Shine pearl gloss',
    tag: 'Soft sheen',
  },
  '/InteriorWallPaint/FineSquareInterriorEmulsion': {
    image: '/images/products/fine-square-emulsion-living.jpg',
    alt: 'Study walls finished in Fine Square Interior Emulsion',
    tag: 'Everyday',
  },
  '/InteriorWallPaint/CeilingWhite': {
    image: '/images/TP-livingroom.jpg',
    alt: 'A bright ceiling finished in Ceiling White',
    tag: 'Ceilings',
  },
  '/InteriorWallPaint/SilkShineSpecialEffectpaints': {
    image: '/images/TR-livingroom.jpg',
    alt: 'A feature wall finished with Silk Shine Special Effect paint',
    tag: 'Special effect',
  },
  '/ExteriorWallPaint/TitaniumOne': {
    image: '/images/products/titanium-one-rain-wall.jpg',
    alt: 'Villa elevation coated in Titanium One exterior emulsion',
    tag: 'Flagship',
  },
  '/ExteriorWallPaint/UltraWRPExteriorEmulsion': {
    image: '/images/products/ultra-wrp-site.jpg',
    alt: 'Beach house elevation coated in Ultra WRP water-repellent emulsion',
    tag: 'Water repellent',
  },
  '/ExteriorWallPaint/ACMEExteriorEmulsion': {
    image: '/images/products/acme-villa.jpg',
    alt: 'Country house elevation coated in ACME Exterior Emulsion',
    tag: 'All-weather',
  },
  '/ExteriorWallPaint/SelectMattFinish': {
    image: exteriorImages.arched,
    alt: 'Arched residence coated in Select Matt Finish',
    tag: 'Matt',
  },
  '/ExteriorWallPaint/FineSquareExteriorEmulsion': {
    image: '/images/products/fine-square-emulsion-house.jpg',
    alt: 'Modern cube house coated in Fine Square Exterior Emulsion',
    tag: '15-year',
  },
};

/** Products featured on the homepage, in display order (routes from the catalogue). */
export const featuredInteriorRoutes = [
  '/InteriorWallPaint/EasyWashLuxuryEmulsion',
  '/InteriorWallPaint/SilkShine_PearlGloss',
  '/InteriorWallPaint/FineSquareInterriorEmulsion',
];

export const featuredExteriorRoutes = [
  '/ExteriorWallPaint/TitaniumOne',
  '/ExteriorWallPaint/UltraWRPExteriorEmulsion',
  '/ExteriorWallPaint/ACMEExteriorEmulsion',
  '/ExteriorWallPaint/PremiumEmlusion',
];

export const galleryImages = {
  interiorLiving: '/images/web/auto/tf-livingroom.jpg',
  interiorBedroom: '/images/TF-bedroom.jpg',
  interiorLounge: '/images/TP-livingroom.jpg',
  interiorStudy: '/images/TR-livingroom.jpg',
  exteriorVilla: '/images/Crafted Cube-A.jpg',
  exteriorHouse: '/images/Beach House-A.jpg',
};

export const finishImages = {
  textures: '/images/Wall-Textures.webp',
  wood: '/images/Wood-Paints.webp',
  wallpapers: '/images/Wallpapers.webp',
  colourCatalogue: '/images/Colour-Catalogue.png',
};

export const ctaImages = {
  calculator: '/images/web/auto/calculator-banner.jpg',
  certificate: '/images/certificate.jpeg',
};
